import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaUserCircle } from "react-icons/fa";
import { Masonry } from "masonic";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AdmissionInfoCard from "./components/AdmissionInfoCard";
import AdmissionRoomsCard from "./components/AdmissionRoomsCard";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import AuthContext from "../../../../context/AuthProvider";
import { MdCheck, MdClose } from "react-icons/md";
import { 
  Box, Typography, Button, Avatar, Table, TableBody, 
  TableCell, TableHead, TableRow, Dialog, TextField, 
  FormControlLabel, Checkbox 
} from "@mui/material";
import AdmissionContactsTable from "./components/AdmissionContactsTable";
import { LoadingDots } from "../../../../components/general";
export const AdmissionChildDetails = () => {
  const { childId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { school } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const [childData, setChildData] = useState(null);
  const [childCardData, setChildCardData] = useState([]);
  const [parentCardData, setParentCardData] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isPending = childData?.status === "pending";
  const canEdit = isPending;
  const canEditParents = childData?.parents_approved === false && isPending;
  const canEditParent1=canEditParents && !childData?.parent1?.is_existing;
  const canEditParent2=canEditParents && !childData?.parent2?.is_existing;

  // Fetch child details
  const fetchChildDetails = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setNotFound(false);

      const [childRes, roomsRes, gendersRes, nationalitiesRes, countriesRes] =
        await Promise.all([
          axiosPrivate.get(
            `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${childId}/full-details/`
          ),
          axiosPrivate.get(
            `${process.env.REACT_APP_BASE_URL}/en/api/nursery/classes/?nursery_id=${school?.id}`
          ),
          axiosPrivate.get(
            `${process.env.REACT_APP_BASE_URL}/en/api/nursery/genders`
          ),
          axiosPrivate.get(
            `${process.env.REACT_APP_BASE_URL}/en/api/nursery/nationalities`
          ),
          axiosPrivate.get(
            `${process.env.REACT_APP_BASE_URL}/en/api/nursery/countries`
          ),
        ]);

      const child = childRes.data.child;
      setChildData(child);

      const rooms = roomsRes.data.map((room) => ({
        id: room.id,
        room: room.ClassName,
      }));

      const genders = gendersRes.data.genders.map((g) => ({
        id: g.id,
        name: g.Name,
      }));

      const nationalities = nationalitiesRes.data.nationalities.map((n) => ({
        id: n.id,
        name: n.Name,
        Name_ar: n.Name_ar,
      }));

      const countries = countriesRes.data.map((c) => ({
        id: c.code,
        name: c.name,
      }));

      // Child data section
      const personalInfo = [
        { label: "Name", value: child.Name || "", editable: true },
        {
          label: "BirthDate",
          value: child.BirthDate,
          type: "date",
          editable: true,
        },
        {
          label: "Age",
          value: child.age_display,
          editable: false,
        },
        {
          label: "Gender",
          value: child.Gender,
          type: "select",
          options: genders,
          editable: true,
        },
        {
          label: "Country",
          value: child.countries,
          type: "select",
          options: countries,
          editable: true,
        },
        {
          label: "Nationality",
          value: child.Nationality,
          type: "select",
          options: nationalities,
          editable: true,
        },
        { label: "Allergies", value: child.Allergies, editable: true },
        { label: "Notes", value: child.Notes, editable: true },
        { label: "Medications", value: child.Medications, editable: true },
        { label: "Doctor Name", value: child.DoctorName, editable: true },
        {
          label: "Doctor Phone No.",
          value: child.DoctorPhone,
          editable: true,
        },
      ];

      const address = [
        { label: "Street", value: child.Street, editable: true },
        { label: "Appartment", value: child.AppartmentHouse, editable: true },
        { label: "City", value: child.City, editable: true },
        { label: "State", value: child.State, editable: true },
        { label: "Zip Code", value: child.ZipCode, editable: true },
      ];

      const currentRooms = child.Classes
        ? child.Classes.map((roomId) => {
            const room = rooms.find((r) => r.id === roomId);
            return room || null;
          }).filter(Boolean)
        : [];

      const childCards = [
        { type: "info", title: "Personal Info", data: personalInfo },
        { type: "rooms", allRooms: rooms, initialSelected: currentRooms },
        { type: "info", title: "Address", data: address, fullWidth: true },
      ];

      setChildCardData(childCards);

      // Parent data section
      const parentCards = [];

      // Parent 1 data
      if (child.parent1) {
        const parent1Data = [
          {
            label: "Name",
            value: child.parent1.name || "",
            editable: true,
          },
          { label: "Email", value: child.parent1.email, editable: true },
          { label: "Phone", value: child.parent1.phone, editable: true },
          { label: "Address", value: child.parent1.address, editable: true },
          {
            label: "Gender",
            value: child.parent1.gender_id,
            type: "select",
            options: genders,
            editable: true,
          },
          {
            label: "Country",
            value: child.parent1.country,
            type: "select",
            options: countries,
            editable: true,
          },
        ];

        parentCards.push({
          type: "info",
          title: "Parent 1",
          data: parent1Data,
          parentNumber: 1,
          halfWidth: true,
          showApproval: !child.parents_approved && isPending,
          isApproved: child.parent1?.is_approved,
        });
      }

      // Parent 2 data
      if (child.parent2 && child.parent2.email) {
        const parent2Data = [
          {
            label: "Name",
            value: child.parent2.name || "",
            editable: true,
          },
          { label: "Email", value: child.parent2.email, editable: true },
          { label: "Phone", value: child.parent2.phone, editable: true },
          { label: "Address", value: child.parent2.address, editable: true },
          {
            label: "Gender",
            value: child.parent2.gender_id,
            type: "select",
            options: genders,
            editable: true,
          },
          {
            label: "Country",
            value: child.parent2.country,
            type: "select",
            options: countries,
            editable: true,
          },
        ];

        parentCards.push({
          type: "info",
          title: "Parent 2",
          data: parent2Data,
          parentNumber: 2,
          halfWidth: true,
          showApproval: !child.parents_approved && isPending,
          isApproved: child.parent2?.is_approved,
        });
      }

      setParentCardData(parentCards);

      // Contacts - always read-only
      const contactsData = child.contacts || [];
      setContacts(contactsData);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(isAr ? "فشل في تحميل تفاصيل الطلب" : "Failed to load admission details");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [childId, school?.id, axiosPrivate]);

  useEffect(() => {
    if (school?.id) {
      fetchChildDetails();
    }
  }, [fetchChildDetails, school?.id]);

  // Save handlers
  const handleChildSave = async (updates) => {
    try {
      await axiosPrivate.patch(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${childId}/`,
        updates
      );
      toast.success(isAr ? "تم تحديث معلومات الطفل بنجاح" : "Child information updated successfully");
      fetchChildDetails(true); // Refresh data
    } catch (error) {
      console.error("Error updating child:", error);
      toast.error(isAr ? "فشل في تحديث معلومات الطفل" : "Failed to update child information");
      throw error;
    }
  };

  const handleParentSave = async (updates, parentNumber) => {
    try {
      await axiosPrivate.patch(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${childId}/parent${parentNumber}/`,
        updates
      );
      toast.success(isAr ? `تم تحديث معلومات الوالد ${parentNumber} بنجاح` : `Parent ${parentNumber} information updated successfully`);
      fetchChildDetails(true); // Refresh data
    } catch (error) {
      toast.error(isAr ? `فشل في تحديث معلومات الوالد ${parentNumber}` : `Failed to update parent ${parentNumber} information`);
      throw error;
    }
  };

  // Action handlers
  const handleApprove = async () => {
    try {
      await axiosPrivate.post(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${childData.id}/approve/`
      );
      toast.success(isAr ? "تمت الموافقة على الطلب بنجاح" : "Application approved successfully");
      navigate(-1);
    } catch (error) {
      console.error("Error approving:", error);
      toast.error(isAr ? "فشل في الموافقة على الطلب" : "Failed to approve application");
    }
  };

  const handleReject = async () => {
    try {
      await axiosPrivate.post(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${childData.id}/reject/`
      );
      toast.success(isAr ? "تم رفض الطلب بنجاح" : "Application rejected successfully");
      navigate(-1);
    } catch (error) {
      toast.error(isAr ? "فشل في رفض الطلب" : "Failed to reject application");
    }
  };

  const fullWidthChildCards = childCardData.filter((card) => card.fullWidth);
  const masonryChildCards = childCardData.filter((card) => !card.fullWidth);

  if (loading) {
    return (
      <LoadingDots />
    );
  }

  if (notFound) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
        textAlign="center"
      >
        <Typography variant="h4" color="primary" gutterBottom>
          Admission Not Found
        </Typography>
        <Typography variant="body2" color="textSecondary">
          The admission application you're looking for doesn't exist.
        </Typography>
      </Box>
    );
  }
 
  

  return (
    <div className="p-5 d-block ms-auto px-4" style={{ minHeight: "70vh" }}>
      {/* Header with status badge and action buttons */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: "2%", mb: 4 }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box position="relative" display="inline-block">
            {childData?.ProfileImg ? (
              <Avatar
                src={childData.ProfileImg}
                alt="img"
                sx={{ width: 85, height: 85 }}
              />
            ) : (
              <FaUserCircle size={85} />
            )}
          </Box>

          <Box>
            <Typography variant="h5" fontWeight="bold">
              {childData?.Name}
            </Typography>
            <Box mt={1}>
              <span
                style={{
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "600",
                  backgroundColor:
                    childData?.status === "approved"
                      ? "#e8f5e9"
                      : childData?.status === "rejected"
                      ? "#ffebee"
                      : "#fff9e6",
                  color:
                    childData?.status === "approved"
                      ? "#2e7d32"
                      : childData?.status === "rejected"
                      ? "#c62828"
                      : "#f57c00",
                  border: `1px solid ${
                    childData?.status === "approved"
                      ? "#c3e6cb"
                      : childData?.status === "rejected"
                      ? "#f5c6cb"
                      : "#ffeaa7"
                  }`,
                }}
              >
                {t(`status.${childData?.status}`) || childData?.status_display}
              </span>
            </Box>
          </Box>
        </Box>

      {isPending && (
        <Box display="flex" gap={2} mt={2}>
          
          <Button
            variant="contained"
            startIcon={<MdCheck size={20} />} 
            sx={{
              backgroundColor: "#e8f5e9", 
              color: "#2e7d32",           
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "10px",      
              padding: "8px 20px",
              boxShadow: "none",          
              border: "1px solid #c8e6c9",
              transition: "all 0.3s ease",
              "&:hover": { 
                backgroundColor: "#2e7d32", 
                color: "#fff",
                boxShadow: "0 4px 12px rgba(46, 125, 50, 0.2)" 
              },
            }}
            onClick={handleApprove}
          >
            {isAr ? "الموافقة على الطلب" : "Approve Application"}
          </Button>

          <Button
            variant="contained"
            startIcon={<MdClose size={20} />} 
            sx={{
              backgroundColor: "#ffebee", 
              color: "#c62828",          
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "10px",
              padding: "8px 20px",
              boxShadow: "none",
              border: "1px solid #ffcdd2",
              transition: "all 0.3s ease",
              "&:hover": { 
                backgroundColor: "#c62828", 
                color: "#fff",
                boxShadow: "0 4px 12px rgba(198, 40, 40, 0.2)" 
              },
            }}
            onClick={handleReject}
          >
            {isAr ? "رفض الطلب" : "Reject Application"}
          </Button>
        </Box>
      )}
      </Box>

      <div style={{ padding: "35px" }}>
        {/* Child Data Section */}
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
          {isAr ? "بيانات الطفل" : "Child Information"}
        </Typography>

        
        <Masonry
          items={masonryChildCards}
          columnGutter={18}
          columnWidth={400}
          render={({ data }) => (
            <div>
              {data.type === "info" ? (
                <AdmissionInfoCard
                  title={data.title}
                  editable={canEdit}
                  data={data.data}
                  onSave={handleChildSave}
                />
              ) : (
                <AdmissionRoomsCard
                  allRooms={data.allRooms}
                  initialSelected={data.initialSelected}
                  onSave={handleChildSave}
                  editable={canEdit}
                />
              )}
            </div>
          )}
        />

        {/* {fullWidthChildCards.map((data, idx) => (
          <div key={`child-full-${idx}`} style={{ marginTop: "18px" }}>
            <AdmissionInfoCard
              title={data.title}
              editable={canEdit}
              data={data.data}
              onSave={handleChildSave}
            />
          </div>
        ))} */}

        {/* Parents Section */}
        {parentCardData.length > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 5,
                mb: 2,
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                {isAr ? "بيانات أولياء الأمور" : "Parents Information"}
              </Typography>

              {childData?.parents_approved && isPending && (
                <span
                  style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "600",
                    backgroundColor: "#d1f8d5ff",
                    color: "#175b1dff",
                    border: "1px solid #a7ffb6ff",
                  }}
                >
                  Approved
                </span>
              )}
            </Box>

            {/* Parent Cards - Side by Side */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  parentCardData.length === 2 ? "1fr 1fr" : "1fr",
                gap: 2,
                mb: 3,
              }}
            >
              {parentCardData.map((data, idx) => {
                const isEditable = data.parentNumber === 1 ? canEditParent1 : canEditParent2;
                return (
                <div key={`parent-${idx}`}>
                  <AdmissionInfoCard
                    title={data.title}
                    editable={isEditable}
                    data={data.data}
                    onSave={(updates) => handleParentSave(updates, data.parentNumber)}
                  />
                </div>
                );
             })}
            </Box>
          </>
        )}

        {/* Contacts Section */}
        {contacts.length > 0 && (
          <AdmissionContactsTable 
            contacts={contacts}
            childId={childId}
            axiosPrivate={axiosPrivate}
            onUpdateSuccess={fetchChildDetails} 
            canEdit={canEdit} 
          />
        )}
      </div>
    </div>
  );
};